import { useEffect, useState, useMemo } from "react";
import { getThemeConfig } from "@/lib/theme-config";

interface Balloon {
  id: number;
  x: number;
  color: string;
  delay: number;
  duration: number;
  size: number;
}

interface Sparkle {
  id: number;
  x: number;
  y: number;
  delay: number;
}

interface Props {
  theme?: string | null;
}

const FloatingDecorations = ({ theme = "classic" }: Props) => {
  const [visible, setVisible] = useState(false);
  const [balloons, setBalloons] = useState<Balloon[]>([]);
  const [sparkles, setSparkles] = useState<Sparkle[]>([]);

  const config = useMemo(() => getThemeConfig(theme), [theme]);
  const balloonColors = config.balloonColors;
  const decorEmojis = config.decorations;

  useEffect(() => {
    // Delay decorations until after LCP to avoid competing for main thread
    const timer = requestIdleCallback?.(() => setVisible(true)) ?? setTimeout(() => setVisible(true), 2000);
    return () => {
      if (typeof timer === 'number') {
        cancelIdleCallback?.(timer) ?? clearTimeout(timer);
      }
    };
  }, []);

  useEffect(() => {
    if (!visible) return;
    
    // Fewer balloons for better performance
    setBalloons(Array.from({ length: 3 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      color: balloonColors[Math.floor(Math.random() * balloonColors.length)],
      delay: Math.random() * 5,
      duration: 15 + Math.random() * 10,
      size: 30 + Math.random() * 20,
    })));

    setSparkles(Array.from({ length: 4 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 3,
    })));
  }, [balloonColors, visible]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0" style={{ contain: 'strict' }}>
      {/* Balloons */}
      {balloons.map((balloon) => (
        <div
          key={balloon.id}
          className="absolute animate-float-up will-change-transform"
          style={{
            left: `${balloon.x}%`,
            bottom: "-100px",
            animationDelay: `${balloon.delay}s`,
            animationDuration: `${balloon.duration}s`,
          }}
        >
          <div
            className="relative"
            style={{
              width: `${balloon.size}px`,
              height: `${balloon.size * 1.2}px`,
            }}
          >
            <div
              className="absolute inset-0 rounded-full opacity-80"
              style={{
                backgroundColor: balloon.color,
                boxShadow: `inset -5px -5px 15px rgba(0,0,0,0.2), inset 5px 5px 15px rgba(255,255,255,0.3)`,
              }}
            />
            <div className="absolute top-2 left-2 w-3 h-3 rounded-full bg-white/40" />
            <div
              className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full"
              style={{
                width: 0,
                height: 0,
                borderLeft: "4px solid transparent",
                borderRight: "4px solid transparent",
                borderTop: `8px solid ${balloon.color}`,
              }}
            />
          </div>
        </div>
      ))}

      {/* Sparkles only */}
      {sparkles.map((sparkle) => (
        <div
          key={sparkle.id}
          className="absolute animate-sparkle will-change-transform"
          style={{
            left: `${sparkle.x}%`,
            top: `${sparkle.y}%`,
            animationDelay: `${sparkle.delay}s`,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-yellow-400">
            <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" fill="currentColor" />
          </svg>
        </div>
      ))}

      {/* Theme emojis - reduced count */}
      {decorEmojis.slice(0, 2).map((emoji, i) => (
        <div
          key={`emoji-${i}`}
          className="absolute animate-float-up text-2xl will-change-transform"
          style={{
            left: `${10 + i * 20}%`,
            bottom: "-50px",
            animationDelay: `${3 + i * 2}s`,
            animationDuration: `${20 + i * 3}s`,
          }}
        >
          {emoji}
        </div>
      ))}
    </div>
  );
};

export default FloatingDecorations;
